import React from "react";
import { createBLoC, useInitBLoC, IBLoC, BLoCCallback, BLoCBase } from "@rsm";
import { ResultsModuleTypes, ResultsModuleTypes as T } from "./ResultsModule.types";
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs";
import {
  odataAct,
  odataDeleteOne,
  odataFirst,
  odataFuncList,
  odataFuncOne,
  odataList,
  odataPatchOne,
  odataPostOne,
} from "services/odataApi";
import { NavigateFunction, useParams } from "react-router-dom";
import { getToggleble } from "rsm/getToggleable";
import { SearchQueryManager, useQueryManager } from "hooks/useQueryManager";
import { useMainNavigate } from "hooks/useMainNavigate";
import { ResultsModuleHelpers as H, ResultsModuleHelpers } from "./ResultsModule.helpers";
import { toast, toastSuccess } from "components/ToastProvider/ToastProvider";
import { ApiSchema } from "@api-types";
import { differenceInCalendarDays } from "date-fns";
import { confirmDialog } from "primereact/confirmdialog";
import { ResultsModuleRequests } from "./ResultsModule.requests";
import { Globals } from "types/globals";
import { getProjectLink } from "components/ProjectLink/ProjectLink";
import { MatIcon } from "components/MatIcon/MatIcon";
import { FiltersHelper } from "helpers/filtersHelper";
import { FiltersWrapper } from "types/filters-wrapper";
import { debounceAfterFirstEmission } from "helpers/rxjsHelper";
import { hasFeature$ } from "helpers/hasFeature";
import { hasPolicy$ } from "helpers/hasPolicy";
import { useChartDownloadButtonBLoC } from "components/ChartDownloadButton/ChartDownloadButton.bloc";
import { getWebsocketService } from "services/websocket.service";
import { getSegmentService } from "services/segment.service";

class BLoC extends BLoCBase<T.State> implements IBLoC {
  /* Props from state */
  public readonly splitBy$ = this.manager.getObject$("splitBy", undefined);
  public readonly selectedMetricQuestionsIds$ = this.getState$("selectedMetricQuestionsIds").pipe(
    distinctUntilChanged(),
  );
  public readonly maxSelectedMetrics$ = this.getState$("maxSelectedMetrics").pipe(distinctUntilChanged());
  public readonly exportSidepanelVisible$ =
    this.getState$("exportSidepanelVisible").pipe(distinctUntilChanged());
  public readonly metricVisualization$ = this.getState$("metricVisualization").pipe(distinctUntilChanged());
  public readonly benchmarkType$ = this.getState$("benchmarkType").pipe(distinctUntilChanged());
  public readonly benchmarkTypeSelection$ = this.getState$("benchmarkTypeSelection");
  public readonly selectedPanelType$ = this.manager.getString$("selectedPanelType") as Observable<
    ApiSchema.PanelType | undefined
  >;
  public readonly segmentId$ = this.manager.getNumber$("segmentId");
  public readonly selectedQuestionFilterKey$ = this.getState$("selectedQuestionFilterKey");
  public readonly selectedProjectGroupSegmentIds$ = this.manager.getObject$(
    "selectedProjectGroupSegmentIds",
    [],
  );
  public readonly selectedTestOptionIds$ = this.manager
    .getObject$("selectedTestOptionIds", [])
    .pipe(map((a) => (a?.length ? a : null)));
  public readonly selectedAdQuestionIds$ = this.manager.getObject$("selectedAdQuestionIds", []);
  public readonly selectedCompareSections$ = this.manager.getObject$("selectedCompareSections", []);
  public readonly selectedQuestionOptionFilterIds$ = this.manager.getObject$(
    "selectedQuestionOptionFilterIds",
    {},
  );
  public readonly selectedFilterIds$ = this.manager.getObject$("selectedFilterIds", {});
  public readonly segments$ = this.getState$("segments");
  public readonly schedulingState$ = this.getState$("schedulingState");
  public readonly schedulingPanelOption$ = this.getState$("schedulingPanelOption");
  public readonly selectedSurveyRunIds$ = this.manager.getObject$("selectedSurveyRunIds", undefined);
  public readonly testInfoSidepanelVisible$ = this.getState$("testInfoSidepanelVisible");
  public readonly benchmarkSidepanelVisible$ = this.getState$("benchmarkSidepanelVisible");
  public readonly followersSidePanelVisible$ = this.getState$("followersSidePanelVisible");
  public readonly narrativeReturn$ = this.getState$("narrativeReturn");
  public getCurrentNarrativeReturn = () => this.currentState("narrativeReturn");

  public readonly segmentSidepanel$ = this.getState$("segmentSidepanel");
  public readonly schedulingPanelVisible$ = this.getState$("schedulingPanelVisible");
  public readonly schedulingPanelEditing$ = this.getState$("schedulingPanelEditing");
  public readonly schedulingEditPanelVisible$ = this.getState$("schedulingEditPanelVisible");
  public readonly selectedDates$ = this.manager.getObject$("selectedDates", undefined).pipe(
    map((value): [Date | null, Date | null] | undefined => {
      if (!value) return undefined;
      const [d1, d2] = value;
      return [d1 !== null ? new Date(d1) : null, d2 !== null ? new Date(d2) : null];
    }),
  );

  public setNarrativeReturn = (
    value: {
      narrativeId: number;
      exportId?: number | null;
      slideNumber?: number | null;
      scrollY: number;
      expandedSectionIds: ReadonlySet<number>;
      destinationPathname: string;
    } | null,
  ) =>
    this.setState(
      "narrativeReturn",
      value ? { ...value, expandedSectionIds: [...value.expandedSectionIds] } : null,
    );

  public readonly projectCost$ = this.shared$(
    odataFirst("getCreditLogs", {}, { orderBy: "projectId" })
      .applyFilter("projectId", "eq", this.projectId)
      .applyGroupBy(["projectId"], {
        by: "creditAmount",
        with: "sum",
        as: "creditAmount",
      })
      .$(),
  );

  public setExportSidepanelVisible = (visible: boolean = true) =>
    this.setState("exportSidepanelVisible", visible);
  public setSelectedMetricQuestionsIds = (questionsIds: number[] | null) => {
    const maxSelectedMetrics = this.currentState("maxSelectedMetrics") ?? 0;
    let limitedQuestionsIds = questionsIds;

    // If maxSelectedMetrics is set and greater than 0, limit the selection
    if (maxSelectedMetrics > 0 && questionsIds?.length) {
      limitedQuestionsIds = questionsIds.slice(0, maxSelectedMetrics);
    }

    if (!limitedQuestionsIds?.length) {
      this.manager.setSearchQuery({ splitBy: null });
    }
    this.setState("selectedMetricQuestionsIds", limitedQuestionsIds);
  };
  public setMaxSelectedMetrics = (maxSelectedMetrics: number) => {
    this.setState("maxSelectedMetrics", maxSelectedMetrics);
  };

  /* Modals */
  public readonly modals = getToggleble<{
    testOptionPreview: { file: Globals.ExistingFile; description?: string | null };
    filterSideBar: {};
    scheduleEditorSideBar: {};
    addSegmentModal: { filters: ApiSchema.SegmentResponsesFilters; id?: number | null };
    testInfoModal: {};
  }>();
  public openTestInfoSidepanel = () => this.setState("testInfoSidepanelVisible", true);
  public closeTestInfoSidepanel = () => this.setState("testInfoSidepanelVisible", false);
  public openBenchmarkSidepanel = () => this.setState("benchmarkSidepanelVisible", true);
  public closeBenchmarkSidepanel = () => this.setState("benchmarkSidepanelVisible", false);
  public openFollowersSidePanel = () => this.setState("followersSidePanelVisible", true);
  public closeFollowersSidePanel = () => this.setState("followersSidePanelVisible", false);
  public readonly exportPlanSidePanelVisible$ = this.getState$("exportPlanSidePanelVisible");
  public openExportPlanSidePanel = () => this.setState("exportPlanSidePanelVisible", true);
  public closeExportPlanSidePanel = () => this.setState("exportPlanSidePanelVisible", false);
  public closeSegmentSidepanel = () => this.setState("segmentSidepanel", undefined);
  public openSchedulingPanel = () => this.setState("schedulingPanelVisible", true);
  public closeSchedulingPanel = () => this.setState("schedulingPanelVisible", false);
  public openSchedulingEditPanel = () => this.setState("schedulingEditPanelVisible", true);
  public closeSchedulingEditPanel = () => this.setState("schedulingEditPanelVisible", false);
  public readonly getModal$ = this.modals.getState$;

  /* Fetched state */
  public readonly project$ = this.getState$("project").pipe(filter((a) => !!a));
  public readonly activeSummaryType$ = this.getState$("activeSummaryType").pipe(distinctUntilChanged());

  public readonly projectGroupSegments$ = this.shared$(
    this.project$.pipe(
      switchMap((p) =>
        p?.projectGroupId ? ResultsModuleRequests.getProjectGroupSegments$(p.projectGroupId) : of(null),
      ),
    ),
  );

  public setActiveSummaryType = (activeSummary: ApiSchema.AiSummaryTypes | null) => {
    this.setState("activeSummaryType", activeSummary);
  };

  private readonly fetchProject = () => {
    this.addSub(
      ResultsModuleRequests.getProject$(this.projectId).subscribe((p) => {
        if (!p) {
          toast({
            severity: "error",
            icon: <MatIcon className="mt-2">error_outline</MatIcon>,
            detail: "Project not found",
          });
          void this.navigate("/");
          return;
        }
        this.setState("project", p);
        this.fetchSchedulingState();
      }),
    );
  };
  public readonly projectsRegions$ = this.project$.pipe(
    filter((p) => !!p),
    map((p) => {
      const schedulesFiltered = p?.projectSchedules?.filter((r) => r.surveyProcessingId);
      const schedules = schedulesFiltered?.length ? schedulesFiltered : p?.projectSchedules;
      return schedules?.reduce(
        (p, r) => {
          p[r.regionId || 0] = r.region?.regionName || "n/a";
          return p;
        },
        {} as { [key in number]: string },
      );
    }),
  );

  public readonly selectedRegionId$ = this.shared$(
    this.manager
      .getNumber$("selectedRegionId")
      .pipe(filter((a): a is number => !!a))
      .pipe(distinctUntilChanged()),
  );

  public readonly curRegionSegments$ = this.shared$(
    combineLatest([this.selectedRegionId$, this.segments$]).pipe(
      map(([regionId, segments]) =>
        segments?.filter(
          (s) => !s.responsesFilters?.regionIds?.length || s.responsesFilters.regionIds.includes(regionId),
        ),
      ),
    ),
  );

  public readonly averageStandardQuestionData$ = this.shared$(
    combineLatest([this.selectedRegionId$, this.project$]).pipe(
      debounceAfterFirstEmission(H.DebounceTimeMs),
      filter(([_, project]) => !!project),
      switchMap(([regionId, project]) =>
        ResultsModuleRequests.getStandardQuestionData$(project.id, [regionId]).pipe(
          map((v) => {
            const reduced = v.reduce(
              (prev, c) => {
                if (!prev[c.standardQuestionId ?? c.questionId]?.length) {
                  prev[c.standardQuestionId ?? c.questionId] = [];
                }
                prev[c.standardQuestionId ?? c.questionId]!.push(c);
                return prev;
              },
              {} as { [key in number]: ResultsModuleRequests.StandardQuestionData[] },
            );
            return Object.keys(reduced)
              .filter((c) => c !== null)
              .map(Number)
              .map((c) => {
                const scores = reduced[c];
                const affirmativeScores = scores?.filter((c) => c.affirmative);
                const topScore = scores
                  ?.filter((c) => c.affirmative)
                  ?.sort((a, b) => (a.answerValue ?? 0) - (b.answerValue ?? 0))?.[0];
                const percentageTop1 = topScore?.percentage ?? 0 / 100;
                const percentageTop2 =
                  (affirmativeScores?.reduce((prev, c) => prev + (c.percentage ?? 0), 0) ?? 0) / 100;
                return {
                  regionId: regionId,
                  benchmarkTypeId: c,
                  benchmarkName: "Study average",
                  projectCount: 1,
                  isAvailable: true,
                  benchmarkType: "Study average",
                  projectType: "",
                  regionCode: "",
                  standardQuestionId: scores?.[0]?.standardQuestionId ?? null,
                  questionId: scores?.[0]?.questionId ?? null,
                  top1Percentage: percentageTop1,
                  top1PercentageTotal: percentageTop1,
                  top1Weighted: topScore?.weight ?? 0,
                  top2Percentage: percentageTop2,
                  top2PercentageTotal: percentageTop2,
                  top2Weighted: affirmativeScores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                  totalWeight: scores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                } as ApiSchema.BenchmarkResult;
              });
          }),
        ),
      ),
    ),
  );

  public readonly benchmarks$ = this.shared$(
    this.selectedRegionId$.pipe(
      switchMap((regionId) =>
        odataFuncList("funcProjectsBenchmarks", {
          key: this.projectId,
          regionIds: [regionId],
        }).$(),
      ),
      map((c) => [
        ...c.value,
        { top2Percentage: 50, benchmarkType: "None" } as ApiSchema.BenchmarkResult & { id: number | null },
      ]),
      tap((bs) => {
        const benchmarkTypeSelectionStr = localStorage.getItem("benchmarkTypeSelection");
        const benchmarkTypeSelection = benchmarkTypeSelectionStr
          ? JSON.parse(benchmarkTypeSelectionStr)
          : null;
        if (
          benchmarkTypeSelection &&
          benchmarkTypeSelection?.type === "benchmark" &&
          bs.find((b) => b.benchmarkTypeId === benchmarkTypeSelection.id && b.isAvailable)
        ) {
          this.setSelectedBenchmarkType(benchmarkTypeSelection);
        } else if (
          bs.find((b) => b.benchmarkType === "IdeallyAverage" && b.isAvailable && b.top2Percentage)
        ) {
          this.setSelectedBenchmarkType({
            type: "benchmark",
            id: bs.find((b) => b.benchmarkType === "IdeallyAverage" && b.isAvailable && b.top2Percentage)
              ?.benchmarkTypeId,
          });
        } else {
          this.setSelectedBenchmarkType({ type: "testAverage" });
        }
      }),
    ),
  );
  public readonly selectedBenchmarks$ = this.shared$(
    combineLatest([this.benchmarks$, this.benchmarkTypeSelection$]).pipe(
      switchMap(([b, btype]) =>
        btype.type === "testAverage"
          ? this.averageStandardQuestionData$
          : btype.type === "testOption" && btype.id
            ? this.getAverageStandardQuestionDataByTestOption$(btype.id)
            : of(b?.filter((bb) => bb.benchmarkTypeId === btype.id)),
      ),
    ),
  );

  public readonly withObjective$ = this.manager.getBoolean$("withObjective", false);
  public setWithObjective = (withObjective: boolean) =>
    this.manager.setSearchQuery({
      withObjective: withObjective ? "true" : null,
    });

  public readonly teriatryBarVisible$ = combineLatest([
    this.selectedFilterIds$,
    this.selectedQuestionOptionFilterIds$,
    this.selectedPanelType$,
  ]).pipe(map(([a, b, c]) => !!Object.keys(a ?? {})?.length || !!Object.keys(b ?? {})?.length || !!c));

  private readonly targetsRaw$ = this.shared$(
    H.getTargetsReqBuilder(this.projectId)
      .$()
      .pipe(map((p) => p.value)),
  );

  public readonly targetOptionIds$ = this.shared$(
    this.targetsRaw$.pipe(
      map((a) =>
        a
          .filter((a) => a.targetMode === true && a.questionOption?.id)
          .map((t) => t.questionOption?.id)
          .flat(),
      ),
    ),
  );

  public readonly targets$ = this.shared$(
    this.targetsRaw$.pipe(
      map((targets) =>
        targets
          .filter((t) => t.targetMode !== false)
          .reduce(
            (p, c) => {
              const questionLabel =
                c.question?.questionTitle || c.systemQuestionOption?.systemQuestion?.questionLabel || "";
              if (!p[questionLabel]) {
                p[questionLabel] = [];
              }
              if (c.systemQuestionOption || c.questionOption) {
                (p[questionLabel] as string[]).push(
                  c.systemQuestionOption?.answerLabel || c.questionOption?.optionText || "",
                );
              } else {
                p[questionLabel] = c.question?.id ?? 0;
              }
              return p;
            },
            {} as { [key in string]: string[] | number },
          ),
      ),
    ),
  );

  public readonly targetOptionIdsByQuestionId$ = this.shared$(
    this.targetsRaw$.pipe(
      map((targets) =>
        targets.reduce(
          (acc, t) => {
            if (t.questionId && t.questionOptionId) {
              const entry = (acc[t.questionId] ??= { include: [], exclude: [] });
              (t.targetMode === false ? entry.exclude : entry.include).push(t.questionOptionId);
            }
            return acc;
          },
          {} as Record<number, { include: number[]; exclude: number[] }>,
        ),
      ),
    ),
  );

  private readonly schedules$ = this.shared$(
    this.project$.pipe(
      switchMap(() =>
        H.getProjectScheduleReqBuilder(this.projectId)
          .$()
          .pipe(map((f) => f.value)),
      ),
    ),
  );

  public readonly futureSchedules$ = this.shared$(
    this.schedules$.pipe(
      map((schedules) =>
        schedules
          .filter((s) => s.surveyRunId === null)
          .reduce(
            (p, c) => {
              const index = p.findIndex((pp) => pp.scheduleDate === c.scheduleDate);
              if (index >= 0) {
                p[index]?.id.push(c.id);
              } else {
                p.push({ ...c, id: [c.id] });
              }
              return p;
            },
            [] as (Omit<ApiSchema.ProjectSchedule, "id"> & { id: number[] })[],
          ),
      ),
    ),
  );

  public readonly selectedDatesSurveyIds$ = this.shared$(
    combineLatest([this.schedules$, this.selectedDates$]).pipe(
      map(([schedules, selectedDates]) => {
        if (!schedules || !selectedDates) return undefined;
        const [start, end] = selectedDates;
        return schedules
          .filter((s) => {
            const date = new Date(s.scheduleDate);
            return (!start || date >= start) && (!end || date <= end);
          })
          .map((s) => s.surveyRunId)
          .filter((id): id is number => typeof id === "number");
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ),
  );

  public readonly testOptions$ = this.shared$(ResultsModuleRequests.getTestOptions$(this.projectId));

  public readonly selectedTestOption$ = this.shared$(
    combineLatest([this.selectedTestOptionIds$, this.testOptions$]).pipe(
      map(([ids, options]) => options?.find((o) => ids?.includes(o.id)) ?? options?.[0]),
    ),
  );

  private readonly elapsedWeeks$ = this.shared$(
    combineLatest([this.project$, this.selectedTestOption$]).pipe(
      map(([p, selectedTestOption]) =>
        (p?.projectSchedules ?? [])
          .filter((sr) => sr.surveyRunId !== null && sr.surveyRunId !== undefined)
          .map((sr) =>
            sr.scheduleDate && selectedTestOption?.launchDate
              ? Math.floor(
                  differenceInCalendarDays(
                    new Date(sr.scheduleDate),
                    new Date(selectedTestOption.launchDate),
                  ) / 7,
                )
              : 0,
          ),
      ),
    ),
  );

  public readonly benchmarkTypes$ = this.shared$(
    combineLatest([this.benchmarks$, this.project$, this.elapsedWeeks$]).pipe(
      map(([b, project, weeks]) =>
        b
          .filter(
            (bb) =>
              !project ||
              !bb.benchmarkTypeId ||
              project.projectType !== "npd" ||
              bb.benchmarkTypeId < 4 ||
              (bb.benchmarkTypeId >= 11 && bb.benchmarkTypeId <= 30) ||
              (bb.benchmarkTypeId === 4 && weeks.some((w) => w <= 8)) ||
              (bb.benchmarkTypeId === 5 && weeks.some((w) => w > 8 && w <= 16)) ||
              (bb.benchmarkTypeId === 6 && weeks.some((w) => w > 16 && w <= 25)) ||
              (bb.benchmarkTypeId === 7 && weeks.some((w) => w > 26)),
          )
          .reduce(
            (p, c) => {
              if (c.benchmarkTypeId) {
                const e = p.find((pp) => pp.id === c.benchmarkTypeId);
                if (e) {
                  e.projectCount = Math.max(e.projectCount, c.projectCount); // Take the highest project count (by standard question)
                  e.isAvailable = e.isAvailable || c.isAvailable; // Allow the benchmark if at least one standard question has it available
                } else {
                  p.push({
                    type: "benchmark",
                    id: c.benchmarkTypeId,
                    label: c.benchmarkName,
                    projectType: project.projectType,
                    regionCode: c.regionCode,
                    projectCount: c.projectCount,
                    isAvailable: c.isAvailable,
                  });
                }
              }
              return p;
            },
            [] as (ResultsModuleTypes.BenchmarkTypeInfo & ResultsModuleTypes.BenchmarkTypeSelection)[],
          ),
      ),
    ),
  );

  public readonly selectedSegment$ = this.shared$(
    combineLatest([
      this.segments$,
      this.segmentId$,
      this.selectedFilterIds$,
      this.selectedQuestionOptionFilterIds$,
      this.selectedPanelType$,
      this.selectedRegionId$,
      this.selectedSurveyRunIds$,
      this.selectedDates$,
    ]).pipe(
      map(
        ([
          segments,
          segmentId,
          filterIds,
          questionOptionIds,
          panelType,
          regionId,
          selectedSurveyRunIds,
          selectedDates,
        ]) => {
          const segment = segments?.find((s) => s.id === segmentId);
          const filter = FiltersHelper.getResponsesFilters(
            filterIds,
            questionOptionIds,
            panelType,
            regionId,
            selectedSurveyRunIds,
            selectedDates,
          );
          return segment
            ? Object.assign({}, segment, {
                matchesCurrentFilters: segments?.find(
                  (s) => s.responsesFilters && FiltersHelper.compareFilters(s.responsesFilters, filter),
                ),
              })
            : null;
        },
      ),
    ),
  );

  public readonly filters$ = this.shared$(
    combineLatest([this.project$, this.selectedRegionId$, this.selectedSurveyRunIds$]).pipe(
      filter(([project]) => !!project),
      distinctUntilChanged(),
      switchMap(([project, selectedRegionId, selectedSurveyRunIds]) =>
        ResultsModuleRequests.getSystemQuestionOptionReqBuilder$(
          selectedRegionId,
          selectedSurveyRunIds,
          project.id,
        ),
      ),
      catchError(() => of(null)),
    ),
  );
  public readonly selectedFilters$ = combineLatest([this.selectedFilterIds$, this.filters$]).pipe(
    map(([ids, filters]) =>
      Object.values(ids).flat()?.length
        ? Object.keys(ids).reduce(
            (p, c) => {
              const f = filters?.filter((f) => ids[c]?.find((id) => id === f.id));
              if (f?.length) {
                p[f[0]?.questionLabel ?? c] = f
                  .filter((a) => !!a.answerLabel)
                  .map((f) => f.answerLabel as never as string)
                  .filter((f) => !!f);
              }
              return p;
            },
            {} as { [key in string]: string[] },
          )
        : null,
    ),
  );
  public readonly filterKeysLabels$ = this.filters$.pipe(
    map((f) =>
      f?.reduce(
        (p, c) =>
          Object.assign(p, {
            [c.questionCode]: c.questionLabel,
          }),
        {} as { [key in string]: string },
      ),
    ),
  );
  public readonly filterKeysTypes$ = this.filters$.pipe(
    map((f) =>
      f?.reduce(
        (p, c) =>
          Object.assign(p, {
            [c.questionCode]: c.systemQuestionTypeId,
          }),
        {} as { [key in string]: Globals.QuestionType },
      ),
    ),
  );
  public readonly filtersByKey$ = this.filters$.pipe(
    map((f) =>
      f?.reduce(
        (r, c) => {
          const key = c.questionCode;
          if (!r[key]) {
            r[key] = [];
          }
          const index = r[key].findIndex((a) => a.answerLabel === c.answerLabel);
          if (index >= 0 && r[key] && r[key][index]) {
            r[key][index] = Object.assign({}, c, { otherIds: [...r[key][index].otherIds, c.id] });
          } else {
            r[key]?.push(Object.assign({}, c, { otherIds: [c.id] }));
          }
          return r;
        },
        {} as {
          [key in string]: (ApiSchema.ProjectSystemQuestionOption & {
            otherIds: number[];
          })[];
        },
      ),
    ),
  );

  public readonly allQuestions$ = this.shared$(
    ResultsModuleRequests.getQuestions$(this.projectId).pipe(
      map(
        (z) =>
          (z
            .map((a) =>
              Object.assign({}, a, {
                question: Object.assign(a.question, {
                  questionOptions: a.question.questionOptions.sort((a, b) => b.id - a.id),
                }),
              }),
            )
            ?.sort((a, b) => {
              const aVal =
                Globals.QuestionPositionSurveyOrder(a.questionPositionId) * 1000000 + (a.sortOrder ?? 0);
              const bVal =
                Globals.QuestionPositionSurveyOrder(b.questionPositionId) * 1000000 + (b.sortOrder ?? 0);
              return aVal - bVal;
            }) as ResultsModuleRequests.ProjectQuestion[]) ?? null,
      ),
      catchError(() => of(null)),
    ),
  );

  public readonly metricQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z
          ?.filter(
            (a) =>
              (a.questionPositionId === Globals.QuestionPositions.CustomMetric &&
                a.question.questionTypeId === Globals.QuestionType.single) ||
              [
                Globals.StandardQuestions.Likeability,
                Globals.StandardQuestions.BrandFit,
                Globals.StandardQuestions.BrandRecognition,
                Globals.StandardQuestions.Desire,
                Globals.StandardQuestions.Distinctiveness,
                Globals.StandardQuestions.PurchaseIntent,
                Globals.StandardQuestions.PurchaseIntentV2,
                Globals.StandardQuestions.Experience,
                Globals.StandardQuestions.Consideration,
                Globals.StandardQuestions.Believability,
              ].includes(a.standardQuestionId ?? 0),
          )
          ?.sort((a, b) => a.sortOrder - b.sortOrder),
      ),
    ),
  );

  public readonly screenerQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) => z?.filter((a) => [Globals.QuestionPositions.Screener].includes(a.questionPositionId ?? 0))),
    ),
  );

  public readonly audienceQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) =>
          [
            Globals.QuestionPositions.BeforeLoop,
            Globals.QuestionPositions.Occasions,
            Globals.QuestionPositions.UsageAndAttitudeTracked,
          ].includes(a.questionPositionId ?? 0),
        ),
      ),
    ),
  );

  public readonly insideTheLoopQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) => [Globals.QuestionPositions.InsideLoop].includes(a.questionPositionId ?? 0)),
      ),
    ),
  );

  public readonly outsideTheLoopQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) =>
          [Globals.QuestionPositions.OutsideLoop, Globals.QuestionPositions.Custom].includes(
            a.questionPositionId ?? 0,
          ),
        ),
      ),
    ),
  );

  public readonly standardQualQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) => a.question.questionTypeId !== Globals.QuestionType.text && a.standardQuestionId !== null,
        ),
      ),
    ),
  );
  public readonly standardTextQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) => a.question.questionTypeId === Globals.QuestionType.text && a.standardQuestionId !== null,
        ),
      ),
    ),
  );

  public readonly followUpQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) => a.standardQuestionId === 10 || [2, 3, 6, 7, 8].includes(a.questionPositionId ?? 0)),
      ),
    ),
  );
  public readonly trialQuestions$ = this.shared$(
    this.allQuestions$.pipe(map((z) => z?.filter((a) => [13, 14].includes(a.standardQuestionId ?? 0)))),
  );
  public readonly awarenessQuestions$ = this.shared$(
    this.allQuestions$.pipe(map((z) => z?.filter((a) => a.questionPositionId === 4))),
  );
  public readonly occasionsQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) => z?.filter((a) => a.questionPositionId === Globals.QuestionPositions.Occasions)),
    ),
  );

  public readonly customMetricTextQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) =>
            a.questionPositionId === Globals.QuestionPositions.CustomMetric &&
            a.question.questionTypeId === Globals.QuestionType.text,
        ),
      ),
    ),
  );

  /* Mirrors the sections rendered by ResultsH2HAudiences */
  public readonly compareAudiencesSections$ = this.shared$(
    combineLatest([
      this.allQuestions$,
      this.followUpQuestions$,
      this.trialQuestions$,
      this.selectedTestOptionIds$,
      this.project$,
    ]).pipe(
      map(([allQuestions, followUpQuestions, trialQuestions, selectedTestOptionIds, project]) => {
        const textQuestions =
          allQuestions?.filter(
            (q) =>
              q.standardQuestionId === Globals.StandardQuestions.WhatYouLikeMost ||
              (q.questionPositionId === Globals.QuestionPositions.CustomMetric &&
                q.question.questionTypeId === Globals.QuestionType.text),
          ) ?? [];
        const loopQuestions = [
          ...(followUpQuestions ?? []).filter((q) =>
            !selectedTestOptionIds
              ? q.questionPositionId === Globals.QuestionPositions.InsideLoop ||
                q.questionPositionId === Globals.QuestionPositions.OutsideLoop ||
                q.questionPositionId === Globals.QuestionPositions.BeforeLoop ||
                q.questionPositionId === Globals.QuestionPositions.Custom ||
                q.questionPositionId === Globals.QuestionPositions.CustomMetric ||
                q.standardQuestionId === Globals.StandardQuestions.AdvertisementOuttake
              : q.questionPositionId === Globals.QuestionPositions.InsideLoop ||
                q.questionPositionId === Globals.QuestionPositions.CustomMetric ||
                q.standardQuestionId === Globals.StandardQuestions.AdvertisementOuttake,
          ),
          ...(trialQuestions ?? []),
        ];
        return H.compareDedupeSections([
          ...(project?.frameworkId !== Globals.Frameworks.PriceSensitivity
            ? [{ key: "test-metrics", label: "Study metrics" }]
            : []),
          ...(H.compareAllowPriceSensitivity(project)
            ? [{ key: "price-sensitivity", label: "Pricing sensitivity" }]
            : []),
          ...[...textQuestions, ...loopQuestions].map(H.compareSectionForQuestion),
        ]);
      }),
    ),
  );

  /* Mirrors the sections rendered by ResultsH2HConcepts */
  public readonly compareConceptsSections$ = this.shared$(
    combineLatest([
      this.allQuestions$,
      this.followUpQuestions$,
      this.customMetricTextQuestions$,
      this.project$,
    ]).pipe(
      map(([allQuestions, followUpQuestions, customMetricTextQuestions, project]) =>
        H.compareDedupeSections([
          ...(project?.frameworkId !== Globals.Frameworks.PriceSensitivity
            ? [{ key: "test-metrics", label: "Study metrics" }]
            : []),
          ...(H.compareAllowPriceSensitivity(project)
            ? [{ key: "price-sensitivity", label: "Pricing sensitivity" }]
            : []),
          ...(allQuestions ?? [])
            .filter((q) => q.standardQuestionId === Globals.StandardQuestions.WhatYouLikeMost)
            .map(H.compareSectionForQuestion),
          ...(customMetricTextQuestions ?? []).map(H.compareSectionForQuestion),
          ...(followUpQuestions ?? [])
            .filter((q) => q.questionPositionId === Globals.QuestionPositions.InsideLoop)
            .map(H.compareSectionForQuestion),
        ]),
      ),
    ),
  );

  public readonly filterQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z
          ?.filter((a) => {
            const isText = a.question.questionTypeId === Globals.QuestionType.text;
            const validPosition = [
              Globals.QuestionPositions.Screener,
              Globals.QuestionPositions.InsideLoop,
              Globals.QuestionPositions.OutsideLoop,
              Globals.QuestionPositions.BeforeLoop,
              Globals.QuestionPositions.Awareness,
              Globals.QuestionPositions.Standard,
              Globals.QuestionPositions.Custom,
              Globals.QuestionPositions.Occasions,
              Globals.QuestionPositions.CustomMetric,
            ].includes(a.questionPositionId ?? 0);

            if (!isText && validPosition) {
              if (a.question.standardQuestionId !== null) {
                a.question.questionOptions = a.question.questionOptions?.filter(
                  (option) => !option.isExcluded,
                );
              }
              return true;
            }

            return false;
          })
          ?.sort((a, b) => {
            const tail = [
              Globals.QuestionPositions.InsideLoop,
              Globals.QuestionPositions.OutsideLoop,
              Globals.QuestionPositions.BeforeLoop,
            ];

            const aPos = a.questionPositionId ?? 0;
            const bPos = b.questionPositionId ?? 0;

            if (aPos === Globals.QuestionPositions.Screener && bPos !== aPos) return -1;
            if (bPos === Globals.QuestionPositions.Screener && aPos !== bPos) return 1;

            const aIsTail = tail.includes(aPos);
            const bIsTail = tail.includes(bPos);
            if (aIsTail && !bIsTail) return 1;
            if (!aIsTail && bIsTail) return -1;

            if (aPos === bPos) {
              return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            }

            return aPos - bPos;
          }),
      ),
      tap((qs) => qs?.length && this.setSelectedQuestionFilterKey(qs[0]!.questionId)),
    ),
  );

  public refreshNarratives = () => this.setState("narrativesRefreshAt", new Date());

  public readonly creatingReport$ = this.getState$("creatingReport");

  public createReport = (onCreated: (narrativeId: number) => void) => {
    this.setState("creatingReport", true);
    this.addSub(
      ResultsModuleRequests.createNarrative$(this.projectId)
        .pipe(first())
        .subscribe({
          next: (narrativeId) => {
            this.setState("creatingReport", false);
            this.refreshNarratives();
            onCreated(narrativeId);
          },
          error: () => this.setState("creatingReport", false),
        }),
    );
  };

  public duplicateNarrative = (narrativeId: number) =>
    ResultsModuleRequests.duplicateNarrative$(narrativeId)
      .pipe(first())
      .subscribe((duplicateId) => {
        this.refreshNarratives();
        void this.navigate(
          ResultsModuleHelpers.internalResultsLink(this.projectId, `narratives/${duplicateId}`),
        );
      });

  public reportLandingLink = (
    narratives: { id: number }[],
    exports: { id: number; narrativeId: number }[],
  ): string | null => {
    const narrativeIds = new Set(narratives.map((n) => n.id));
    const validExports = exports.filter((e) => narrativeIds.has(e.narrativeId));
    const latestExport = validExports.length
      ? validExports.reduce((latest, e) => (e.id > latest.id ? e : latest))
      : undefined;
    if (latestExport)
      return ResultsModuleHelpers.internalResultsLink(
        this.projectId,
        `narratives/${latestExport.narrativeId}/presentations/${latestExport.id}`,
      );
    const latestNarrative = narratives.length
      ? narratives.reduce((latest, n) => (n.id > latest.id ? n : latest))
      : undefined;
    if (latestNarrative)
      return ResultsModuleHelpers.internalResultsLink(this.projectId, `narratives/${latestNarrative.id}`);
    return null;
  };

  public readonly narratives$ = this.shared$(
    combineLatest([
      hasFeature$("ProjectNarratives"),
      hasPolicy$("IdeallyResearch"),
      this.getState$("narrativesRefreshAt"),
    ]).pipe(
      switchMap(([hasFf, isSuper]) =>
        hasFf || isSuper
          ? odataList("getProjectNarratives")
              .filter("projectId", "eq", this.projectId)
              .select("id", "projectId", "title", "createdAt")
              .orderBy("createdAt")
              .$()
              .pipe(map((c) => c.value))
          : of([]),
      ),
    ),
  );

  public refreshNarrativeSections = () => this.setState("narrativeSectionsRefreshAt", new Date());

  public readonly narrativeSections$ = this.shared$(
    combineLatest([this.narratives$, this.getState$("narrativeSectionsRefreshAt")]).pipe(
      switchMap(([narratives]) => {
        const narrativeId = narratives?.[0]?.id;
        if (!narrativeId) return of([] as { id: number; title: string; sortOrder: number }[]);
        return odataFirst("getProjectNarratives")
          .filter("id", "eq", narrativeId)
          .select("id")
          .expandAndSelect(
            "activeProjectNarrativeVersion",
            (v) => v.expandAndSelect("projectNarrativeSections", null, "id", "title", "sortOrder"),
            "id",
          )
          .$()
          .pipe(
            map((n) => {
              const sections = n?.activeProjectNarrativeVersion?.projectNarrativeSections ?? [];
              return [...sections]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((s) => ({ id: s.id, title: s.title, sortOrder: s.sortOrder }));
            }),
          );
      }),
    ),
  );

  public readonly activeNarrativeVersionId$ = this.shared$(
    this.narratives$.pipe(
      switchMap((narratives) => {
        const narrativeId = narratives?.[0]?.id;
        if (!narrativeId) return of(null as number | null);
        return odataFirst("getProjectNarratives")
          .filter("id", "eq", narrativeId)
          .select("id")
          .expandAndSelect("activeProjectNarrativeVersion", null, "id")
          .$()
          .pipe(map((n) => n?.activeProjectNarrativeVersion?.id ?? null));
      }),
    ),
  );

  public refreshNarrativeVersionExports = () => this.setState("narrativeVersionExportsRefreshAt", new Date());

  public readonly narrativeVersionExports$ = this.shared$(
    combineLatest([this.narratives$, this.getState$("narrativeVersionExportsRefreshAt")]).pipe(
      switchMap(([narratives]) => {
        if (!narratives?.length)
          return of(
            [] as {
              id: number;
              completedAt: string | null;
              narrativeId: number;
            }[],
          );
        return odataList("getProjectNarrativeVersionExports")
          .applyFilter("projectId", "eq", this.projectId)
          .select("id", "completedAt")
          .expandAndSelect("projectNarrativeVersion", null, "id", "projectNarrativeId")
          .$()
          .pipe(
            map((r) =>
              (r.value ?? []).map((e) => ({
                id: e.id,
                completedAt: e.completedAt ?? null,
                narrativeId: e.projectNarrativeVersion?.projectNarrativeId ?? 0,
              })),
            ),
          );
      }),
    ),
  );

  public readonly reportLandingLink$ = this.shared$(
    combineLatest([this.narratives$, this.narrativeVersionExports$]).pipe(
      map(([narratives, exports]) => this.reportLandingLink(narratives, exports)),
    ),
  );

  public openReportLanding = () =>
    this.reportLandingLink$.pipe(first()).subscribe((link) => {
      if (link) {
        void this.navigate(link);
        this.setState("pulseReportTabAt", new Date());
      }
    });

  public readonly pulseReportTabAt$ = this.getState$("pulseReportTabAt");

  public readonly testOptionsPickerList$ = this.testOptions$.pipe(
    map((t) => [
      { id: null, conceptText: "All" },
      ...(t?.map((tt) => ({ id: tt.id, conceptText: tt.conceptText ?? "" })) ?? []),
    ]),
  );

  public readonly selectedTestOptions$ = combineLatest([this.selectedTestOptionIds$, this.testOptions$]).pipe(
    map(([ids, options]) => options?.filter((o) => ids?.includes(o.id))?.map((a) => a)),
  );
  public getAverageStandardQuestionDataByTestOption$ = (testOptionId: number) =>
    this.shared$(
      combineLatest([
        this.selectedRegionId$,
        this.project$,
        this.testOptions$,
        this.selectedFilterIds$,
        this.selectedQuestionOptionFilterIds$,
        this.selectedPanelType$,
        this.selectedProjectGroupSegmentIds$,
      ]).pipe(
        debounceAfterFirstEmission(H.DebounceTimeMs),
        filter(([regionId, project, testOptions]) => !!regionId && !!project && !!testOptions?.length),
        switchMap(
          ([regionId, project, testOptions, filters, questionOptions, panelType, categorySegmentIds]) =>
            ResultsModuleRequests.getStandardQuestionData$(
              project.id,
              [regionId],
              testOptionId,
              filters,
              questionOptions,
              panelType,
              categorySegmentIds,
            ).pipe(
              map((v) => {
                const reduced = v.reduce(
                  (prev, c) => {
                    if (!prev[c.standardQuestionId ?? c.questionId]?.length) {
                      prev[c.standardQuestionId ?? c.questionId] = [];
                    }
                    prev[c.standardQuestionId ?? c.questionId]!.push(c);
                    return prev;
                  },
                  {} as { [key in number]: ResultsModuleRequests.StandardQuestionData[] },
                );
                return Object.keys(reduced)
                  .filter((c) => c !== null)
                  .map(Number)
                  .map((c) => {
                    const scores = reduced[c];
                    const affermativeScores = scores?.filter((c) => c.affirmative);
                    const topScore = scores
                      ?.filter((c) => c.affirmative)
                      ?.sort((a, b) => (a.answerValue ?? 0) - (b.answerValue ?? 0))?.[0];
                    const testOption = testOptions?.find((t) => t.id === topScore?.testOptionId);
                    return {
                      regionId: regionId,
                      benchmarkTypeId: c,
                      benchmarkName: `${testOption?.conceptText ?? "Concept"} average`,
                      projectCount: 1,
                      isAvailable: true,
                      benchmarkType: `${testOption?.conceptText ?? "Concept"} average`,
                      projectType: "",
                      regionCode: "",
                      standardQuestionId: scores?.[0]?.standardQuestionId ?? null,
                      questionId: scores?.[0]?.questionId ?? null,
                      testOptionId: topScore?.testOptionId,
                      top1Percentage: (topScore?.percentage ?? 0) / 100,
                      top1PercentageTotal: (topScore?.percentage ?? 0) / 100,
                      top1Weighted: topScore?.weight ?? 0,
                      top2Percentage:
                        (affermativeScores?.reduce((prev, c) => prev + (c.percentage ?? 0), 0) ?? 0) / 100,
                      top2PercentageTotal:
                        (affermativeScores?.reduce((prev, c) => prev + (c.percentage ?? 0), 0) ?? 0) / 100,
                      top2Weighted: affermativeScores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                      totalWeight: scores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                    };
                  });
              }),
            ),
        ),
      ),
    );

  public readonly selectedQuestionOptionFilter$ = combineLatest([
    this.selectedQuestionOptionFilterIds$,
    this.allQuestions$,
  ]).pipe(
    map(([ids, questions]) =>
      Object.values(ids).flat()?.length
        ? Object.keys(ids).reduce(
            (p, c) => {
              const question = questions?.find((q) => q.questionId === Number(c));
              if (question && Object.keys(ids[Number(c)] ?? {}).length) {
                p[question.question.questionTitle] = question.question.questionOptions
                  .filter((o) => Object.keys(ids[Number(c)] ?? {})?.find((a) => Number(a) === o.id))
                  .map((o) => o.optionText ?? "Unknown");
              }
              return p;
            },
            {} as { [key in string]: string[] },
          )
        : null,
    ),
  );

  public readonly totalSurveyRespondentCount$ = this.shared$(
    this.schedules$.pipe(
      switchMap((schedules) =>
        odataList<{ count: number }>("getResponses", { orderBy: "projectId" })
          .applyGroupBy(["projectId", "regionId"], {
            as: "count",
            by: "surveySessionId",
            with: "countdistinct",
          })
          .applyFilter("projectId", "eq", this.projectId)
          .applyFilterIn(
            "regionId",
            schedules.map((s) => s.regionId || 0),
          )
          .$()
          .pipe(map((r) => r?.value?.reduce((acc, curr) => acc + (curr.count ?? 0), 0) ?? 0)),
      ),
    ),
  );

  public readonly projectResponseCountByTestOption$ = this.shared$(
    combineLatest([
      this.selectedFilterIds$.pipe(distinctUntilChanged()),
      this.selectedDates$.pipe(distinctUntilChanged()),
      this.selectedQuestionOptionFilterIds$.pipe(distinctUntilChanged()),
      this.selectedRegionId$.pipe(distinctUntilChanged()),
      this.selectedSurveyRunIds$.pipe(distinctUntilChanged()),
      this.selectedPanelType$.pipe(distinctUntilChanged()),
      this.selectedProjectGroupSegmentIds$.pipe(distinctUntilChanged()),
    ]).pipe(
      debounceAfterFirstEmission(H.DebounceTimeMs),
      switchMap(
        ([
          filterIds,
          dates,
          questionOptionsIds,
          regionId,
          selectedSurveyRunIds,
          panelType,
          selectedProjectGroupSegmentIds,
        ]) => {
          const includeIds = FiltersHelper.filterSubDictionaryByValue(questionOptionsIds, true)
            .map((a) => a.ids)
            .flat();
          const excludeIds = FiltersHelper.filterSubDictionaryByValue(questionOptionsIds, false)
            .map((a) => a.ids)
            .flat();
          return odataFuncOne(
            "funcProjectsResponsesCount",
            {
              key: this.projectId,
              filters: {
                questionOptionsIds: includeIds?.length ? includeIds : undefined,
                excludeQuestionOptionsIds: excludeIds?.length ? excludeIds : undefined,
                filterIds: Object.values(filterIds).flat(),
                surveyIds: selectedSurveyRunIds ?? undefined,
                testOptionIds: undefined,
                regionIds: [regionId],
                dateFrom: (dates?.[0] ?? undefined) as never,
                dateTo: (dates?.[1] ?? undefined) as never,
                panelType: panelType,
                projectGroupSegmentIds: selectedProjectGroupSegmentIds?.length
                  ? selectedProjectGroupSegmentIds
                  : undefined,
              },
            },
            { nullOnError: true },
          )
            .$()
            .pipe(
              // nullOnError makes a failed request emit null instead of cancelling the stream, so
              // consumers can tell "not loaded yet" from "settled with no counts".
              map((responseList) =>
                (responseList ?? []).reduce(
                  (acc, response) => {
                    const testOptionId = response.testOptionId ?? 0;

                    const testGroup = acc[testOptionId] || {
                      count: 0,
                      weight: 0,
                      stdResponsesCount: 0,
                      stdQuestions: [],
                      byScheduleDate: {},
                    };

                    testGroup.weight = Math.max(testGroup.weight, response.weight);
                    testGroup.count = Math.max(testGroup.count, response.count);
                    testGroup.stdQuestions.push(response);
                    if (response.standardQuestionId) {
                      testGroup.stdResponsesCount = Math.max(testGroup.stdResponsesCount, response.count);
                    }

                    (response.byScheduleDate ?? []).forEach((bw) => {
                      const key = H.scheduleDateKey(bw.scheduleDate);
                      testGroup.byScheduleDate[key] = {
                        count: Math.max(testGroup.byScheduleDate[key]?.count || 0, bw.count ?? 0),
                        weight: Math.max(testGroup.byScheduleDate[key]?.weight || 0, bw.weight ?? 0),
                      };
                    });

                    acc[testOptionId] = testGroup;
                    return acc;
                  },
                  {} as Record<
                    number,
                    {
                      count: number;
                      stdResponsesCount: number;
                      weight: number;
                      stdQuestions: ApiSchema.RespondentCount[];
                      byScheduleDate: Record<string, { count: number; weight: number }>;
                    }
                  >,
                ),
              ),
            );
        },
      ),
    ),
  );

  public readonly totalSurveyRespondentsBySelectedTestOptionIds$ = this.shared$(
    combineLatest([
      this.selectedFilterIds$.pipe(distinctUntilChanged()),
      this.selectedDates$.pipe(distinctUntilChanged()),
      this.selectedQuestionOptionFilterIds$.pipe(distinctUntilChanged()),
      this.selectedSurveyRunIds$.pipe(distinctUntilChanged()),
      this.selectedPanelType$.pipe(distinctUntilChanged()),
      this.selectedProjectGroupSegmentIds$.pipe(distinctUntilChanged()),
      this.selectedTestOptionIds$,
      this.selectedRegionId$.pipe(distinctUntilChanged()),
      this.testOptions$,
    ]).pipe(
      debounceAfterFirstEmission(H.DebounceTimeMs),
      switchMap(
        ([
          filterIds,
          dates,
          questionOptionsIds,
          selectedSurveyRunIds,
          panelType,
          selectedProjectGroupSegmentIds,
          selectedTestOptionIds,
          regionId,
          testOptions,
        ]) => {
          const allTestOptionIds = testOptions?.map((t) => t.id) ?? [];
          const testOptionIdsForFilter = selectedTestOptionIds?.length
            ? selectedTestOptionIds
            : allTestOptionIds;
          const includeIds = FiltersHelper.filterSubDictionaryByValue(questionOptionsIds, true)
            .map((a) => a.ids)
            .flat();
          const excludeIds = FiltersHelper.filterSubDictionaryByValue(questionOptionsIds, false)
            .map((a) => a.ids)
            .flat();

          return odataFuncList("funcResponsesWeightSum", {
            projectId: this.projectId,
            filters: {
              questionOptionsIds: includeIds?.length ? includeIds : undefined,
              excludeQuestionOptionsIds: excludeIds?.length ? excludeIds : undefined,
              filterIds: Object.values(filterIds).flat(),
              surveyIds: selectedSurveyRunIds ?? undefined,
              testOptionIds: testOptionIdsForFilter.length ? testOptionIdsForFilter : undefined,
              regionIds: [regionId],
              dateFrom: (dates?.[0] ?? undefined) as never,
              dateTo: (dates?.[1] ?? undefined) as never,
              panelType: panelType,
              projectGroupSegmentIds: selectedProjectGroupSegmentIds?.length
                ? selectedProjectGroupSegmentIds
                : undefined,
            },
          })
            .$()
            .pipe(map((z) => (z.value ?? []).reduce((acc, row) => acc + (row.responsesTotal ?? 0), 0)));
        },
      ),
    ),
  );

  public readonly projectResponseCount$ = combineLatest([
    this.projectResponseCountByTestOption$,
    this.project$,
  ]).pipe(
    map(([responseCountsByTestOption, project]) => {
      const aggregateData = (
        fieldSelector: (data: (typeof responseCountsByTestOption)[number]) => number,
      ): number => {
        const data = Object.fromEntries(
          Object.entries(responseCountsByTestOption).map(([testOptionId, data]) => [
            testOptionId,
            fieldSelector(data),
          ]),
        );
        const values = Object.values(data);
        if (!values.length || !project) return 0;
        if (!project.splitCellQuota && !project.testOptionCells.length) return Math.max(...values);
        if (!project.testOptionCells.length)
          return values.reduce((acc, curr) => acc + curr, 0) / (project.splitCellQuota ?? 1);

        const total = project.testOptionCells.reduce((sum, cell) => {
          const cellSum = Object.entries(data).reduce(
            (subSum, [testOptionId, value]) =>
              cell.testOptions.some((t) => t.id === Number(testOptionId)) ? subSum + value : subSum,
            0,
          );
          return sum + cellSum / (cell.numTestOptionsShown ?? cell.testOptions.length);
        }, 0);
        return total / (project.splitCellQuota ?? project.testOptionCells.length);
      };
      return {
        count: aggregateData((r) => r.count),
        stdResponsesCount: aggregateData((r) => r.stdResponsesCount),
        weight: aggregateData((r) => r.weight),
        stdQuestions: Object.values(responseCountsByTestOption).flatMap((response) => response.stdQuestions),
        byScheduleDate: Object.values(responseCountsByTestOption).reduce(
          (acc, response) => {
            Object.entries(response.byScheduleDate).forEach(([dateKey, stats]) => {
              const prev = acc[dateKey] ?? { count: 0, weight: 0 };

              acc[dateKey] = {
                count: Math.max(prev.count, stats.count),
                weight: Math.max(prev.weight, stats.weight),
              };
            });

            return acc;
          },
          {} as Record<string, { count: number; weight: number }>,
        ),
      };
    }),
  );

  public readonly selectedFilterCount$ = combineLatest([
    this.selectedFilterIds$,
    this.selectedQuestionOptionFilterIds$,
    this.selectedSurveyRunIds$,
    this.selectedPanelType$,
    this.selectedDates$,
    this.selectedProjectGroupSegmentIds$,
  ]).pipe(
    map(
      ([filters, screeners, surveyRunIds, panelType, dates, projectGroupSegmentIds]) =>
        Object.values(filters || {}).filter((f) => !!f?.length).length +
        Object.values(screeners || {}).filter((f) => !!Object.keys(f ?? {})?.length).length +
        (surveyRunIds?.length || 0) +
        (panelType ? 1 : 0) +
        (dates?.[0] || dates?.[1] ? 1 : 0) +
        (projectGroupSegmentIds?.length || 0),
    ),
  );

  public readonly panelTypes$ = this.shared$(
    combineLatest([this.selectedRegionId$, this.project$]).pipe(
      map(([selectedRegionId, project]) =>
        project?.projectSchedules
          ?.filter((w) => w.regionId === selectedRegionId)
          ?.map((w) => Globals.getPanelType(Number(w.surveyRun?.surveySampleProviderId ?? 1)))
          ?.filter((v, i, a) => v && a.indexOf(v) === i),
      ),
    ),
  );
  public readonly filtersWrapper: FiltersWrapper = {
    selectedRegionId$: this.selectedRegionId$,
    selectedFilterIds$: this.selectedFilterIds$,
    selectedQuestionOptionFilterIds$: this.selectedQuestionOptionFilterIds$,
    selectedTestOptionIds$: this.selectedTestOptionIds$,
    selectedPanelType$: this.selectedPanelType$,
    selectedProjectGroupSegmentIds$: this.selectedProjectGroupSegmentIds$,
    selectedSurveyRunIds$: this.selectedSurveyRunIds$,
    selectedUandAFilterIds$: of(null),
    selectedDemandZoneIds$: of([]),
  };

  /* State set functions */
  public setSelectedRegionId = (selectedRegionId: number) => {
    if (Number(this.manager.getCurrentValue("selectedRegionId")) !== selectedRegionId) {
      this.manager.setSearchQuery({
        selectedRegionId,
        selectedFilterIds: {},
        selectedQuestionOptionFilterIds: {},
        selectedDates: undefined,
      });
    }
  };
  public setSelectedTestOptionId = (optionIds: number[] | null) =>
    this.manager.setSearchQuery({ selectedTestOptionIds: optionIds });
  public setSelectedAdQuestionIds = (projectQuestionIds: number[] | null) =>
    this.manager.setSearchQuery({ selectedAdQuestionIds: projectQuestionIds });
  public setSelectedCompareSections = (keys: string[]) =>
    this.manager.setSearchQuery({ selectedCompareSections: keys });
  public toggleSelectedCompareSection = (key: string) => {
    const current = this.manager.getCurrentValueObject("selectedCompareSections", []) ?? [];
    this.setSelectedCompareSections(
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };
  public setSelectedSurveyRunIds = (surveyRunIds: number[] | null) =>
    this.manager.setSearchQuery({
      selectedSurveyRunIds: surveyRunIds,
      selectedDates: undefined,
    });
  public setSelectedDates = (selectedDates: [Date | null, Date | null] | undefined) => {
    const adjustedDates = selectedDates && [
      selectedDates[0] ? new Date(selectedDates[0].setHours(0, 0, 0, 0)) : null,
      selectedDates[1] ? new Date(selectedDates[1].setHours(23, 59, 59, 999)) : null,
    ];

    this.manager.setSearchQuery({
      selectedDates: adjustedDates,
      selectedSurveyRunIds: undefined,
    });
  };
  public setSelectedQuestionFilterKey = (key: number | null) =>
    this.setState("selectedQuestionFilterKey", key);

  public readonly benchmarkMode$ = this.getState$("benchmarkMode").pipe(distinctUntilChanged());
  public setBenchmarkMode = (mode: "percentage" | "index") => {
    this.setState("benchmarkMode", mode);
    localStorage.setItem("benchmarkMode", mode);
  };

  public resetAllFilters = () =>
    this.manager.setSearchQuery({
      selectedFilterIds: {},
      selectedQuestionOptionFilterIds: {},
      selectedPanelType: undefined,
      segmentId: null,
      selectedDates: undefined,
      selectedProjectGroupSegmentIds: undefined,
    });
  public resetFilterIds = (filterKey?: string) =>
    filterKey
      ? this.manager
          .getObject$("selectedFilterIds")
          .pipe(first())
          .subscribe((r) => {
            delete r[filterKey];
            this.manager.setSearchQuery({
              selectedFilterIds: r,
            });
          })
      : this.manager.setSearchQuery({
          selectedFilterIds: {},
          selectedDates: undefined,
          selectedQuestionOptionFilterIds: [],
        });
  public resetQuestionOptionIds = (questionId?: number) =>
    questionId
      ? this.manager
          .getObject$("selectedQuestionOptionFilterIds")
          .pipe(first())
          .subscribe((r) => {
            delete r[questionId];
            this.manager.setSearchQuery({
              selectedQuestionOptionFilterIds: r,
            });
          })
      : this.manager.setSearchQuery({
          selectedQuestionOptionFilterIds: {},
        });

  public setSelectedFilterId = (groupLabel: string, ids: number[]) =>
    this.manager.setSearchQuery({
      selectedFilterIds: Object.assign(
        {},
        JSON.parse(this.manager.getCurrentValue("selectedFilterIds") || "{}"),
        {
          [groupLabel]: ids,
        },
      ),
    });

  public setSelectedQuestionOptionFilterIds = (questionId: number, ids: { [key in number]: boolean }) =>
    this.manager.setSearchQuery({
      selectedQuestionOptionFilterIds: Object.assign(
        {},
        JSON.parse(this.manager.getCurrentValue("selectedQuestionOptionFilterIds") || "{}"),
        {
          [questionId]: ids,
        },
      ),
    });
  public setSelectedPanelType = (type: ApiSchema.PanelType) =>
    this.manager.setSearchQuery({
      selectedPanelType: type,
    });
  public resetPanelType = () =>
    this.manager.setSearchQuery({
      selectedPanelType: undefined,
    });

  /* Modal funcs */
  public openFilterSidebar = () => this.modals.setState("filterSideBar", {});
  public openScheduleEditorSideBar = () => this.modals.setState("scheduleEditorSideBar", {});

  public openTestOptionPreview = (file: Globals.ExistingFile, description?: string | null) =>
    this.modals.setState("testOptionPreview", { file, description });
  public openSegmentSidepanel = (segment?: ResultsModuleRequests.Segment) => {
    combineLatest([
      this.selectedFilterIds$.pipe(first()),
      this.selectedQuestionOptionFilterIds$.pipe(first()),
      this.selectedPanelType$.pipe(first()),
      this.selectedRegionId$.pipe(first()),
      this.selectedSurveyRunIds$.pipe(first()),
      this.selectedDates$.pipe(first()),
      this.selectedProjectGroupSegmentIds$.pipe(first()),
    ]).subscribe(
      ([
        filterIds,
        questionOptionFilterIds,
        panelType,
        regionId,
        selectedSurveyRunIds,
        selectedDates,
        projectGroupSegmentIds,
      ]) =>
        this.setState("segmentSidepanel", {
          id: segment?.id,
          filters:
            segment?.responsesFilters ??
            FiltersHelper.getResponsesFilters(
              filterIds,
              questionOptionFilterIds,
              panelType,
              regionId,
              selectedSurveyRunIds,
              selectedDates,
              projectGroupSegmentIds,
            ),
        }),
    );
  };

  public setSchedulingPanelOption = (option: T.State["schedulingPanelOption"]) => {
    this.setState("schedulingPanelOption", option);
  };

  public closeModal = (refetch?: boolean) => {
    if (refetch) {
      this.fetchProject();
      this.fetchSchedulingState();
    }
    this.closeSchedulingPanel();
    this.closeSchedulingEditPanel();
    this.modals.resetState();
  };

  /* Other functions */
  constructor(
    public projectId: number,
    private readonly navigate: NavigateFunction,
    private readonly manager: SearchQueryManager<T.QueryState>,
    private readonly setDownloadDate: (downloadDate: Date, name?: string) => void,
  ) {
    super({
      metricVisualization: "top2combined",
      benchmarkType: "top2",
      schedulingState: undefined,
      schedulingPanelOption: "upcoming",
      activeSummaryType: null,
      benchmarkMode: (localStorage.getItem("benchmarkMode") as "percentage" | "index") || "percentage",
      maxSelectedMetrics: 0,
      benchmarkTypeSelection: { type: "benchmark", id: 1 },
    });
  }
  public onInit() {
    this.fetchSegments();
    this.fetchProject();

    this.addSub(
      combineLatest([
        this.metricVisualization$,
        this.benchmarkType$,
        this.exportSidepanelVisible$,
        this.selectedMetricQuestionsIds$,
        this.selectedQuestionFilterKey$,
        this.splitBy$,
      ]).subscribe(
        ([
          metricVisualization,
          benchmarkType,
          exportSidepanelVisible,
          selectedMetricQuestionsIds,
          selectedQuestionFilterKey,
          splitBy,
        ]) =>
          getSegmentService().setEnvlope(this, {
            metricVisualization,
            benchmarkType,
            exportSidepanelVisible,
            selectedMetricQuestionsIds,
            selectedQuestionFilterKey,
            splitBy,
          }),
      ),
      "eventTrackingEnvelope",
    );

    this.addSub(
      getWebsocketService().narrativeGeneratedEvent$.subscribe((e) => {
        if (e?.payload?.projectId !== this.projectId) return;
        this.refreshNarratives();
        this.refreshNarrativeSections();
        this.refreshNarrativeVersionExports();
      }),
    );

    this.addSub(
      getWebsocketService()
        .userChannel$.pipe(
          switchMap((channel) => channel.messages$<{ aiThreadId: number }>("AiThreadNewMessage")),
          withLatestFrom(this.narrativeVersionExports$),
          filter(([message, exports]) => !!message && exports.some((e) => e.completedAt === null)),
        )
        .subscribe(() => this.refreshNarrativeVersionExports()),
    );

    // Set default regionId if not set already set in the url
    this.addSub(
      this.projectsRegions$.pipe(first()).subscribe((regions) => {
        const curRegionId = Number(this.manager.getCurrentValue("selectedRegionId"));
        const allowedRegionIds = Object.keys(regions).map(Number);
        if (!curRegionId || !allowedRegionIds.includes(curRegionId)) {
          this.setSelectedRegionId(allowedRegionIds[0] ?? 1);
        }
      }),
    );

    this.addSub(
      combineLatest([this.project$, this.selectedRegionId$])
        .pipe(
          map(([project, regionId]) =>
            ResultsModuleHelpers.getProjectRunDate({
              createdAt: project.createdAt,
              projectSchedules: project.projectSchedules,
              regionId,
            }),
          ),
          filter((runDate): runDate is Date | string => !!runDate),
        )
        .subscribe((runDate) => {
          this.setDownloadDate(new Date(runDate), "default");
        }),
    );

    this.addSub(
      combineLatest([
        this.metricQuestions$,
        this.maxSelectedMetrics$,
        this.selectedMetricQuestionsIds$,
      ]).subscribe(([metricQuestions, maxSelectedMetrics, selectedMetricQuestionsIds]) => {
        const max = maxSelectedMetrics ?? 0;
        if (max > 0 && metricQuestions?.length) {
          if (!selectedMetricQuestionsIds?.length && metricQuestions.length > max) {
            const firstMaxMetrics = metricQuestions.slice(0, max).map((q) => q.questionId);
            this.setState("selectedMetricQuestionsIds", firstMaxMetrics);
          } else if (selectedMetricQuestionsIds?.length && selectedMetricQuestionsIds.length > max) {
            const trimmedIds = selectedMetricQuestionsIds.slice(0, max);
            this.setState("selectedMetricQuestionsIds", trimmedIds);
          }
        }
      }),
    );
  }

  public onDestroy = () => {
    getSegmentService().setEnvlope(this, null);
  };

  public fetchSegments = (setNewSegment?: number) =>
    this.addSub(
      ResultsModuleRequests.getSegments$(this.projectId).subscribe((s) => {
        this.setState("segments", s);
        if (setNewSegment) {
          setTimeout(() => this.setSegment(setNewSegment));
        }
      }),
    );

  public fetchSchedulingState = () =>
    this.addSub(
      ResultsModuleRequests.getSchedulingState$(this.projectId).subscribe((s) =>
        this.setState("schedulingState", s),
      ),
    );

  public segmentToFEFilters$ = (segmentId: number) =>
    this.segments$.pipe(
      filter((s) => !!s),
      map((s) => s.find((s) => s.id === segmentId)),
      first(),
      switchMap((segment) =>
        segment
          ? combineLatest([this.filters$.pipe(first()), this.allQuestions$.pipe(first())]).pipe(
              map(([filters, questions]) => {
                const selectedFilters = segment.responsesFilters?.filterIds?.length
                  ? filters?.filter((f) => segment?.responsesFilters?.filterIds?.includes(f.id))
                  : null;
                const filterQuestion = questions?.filter((q) =>
                  q.question.questionOptions.find(
                    (o) =>
                      segment.responsesFilters?.questionOptionsIds?.includes(o.id) ||
                      segment.responsesFilters?.excludeQuestionOptionsIds?.includes(o.id),
                  ),
                );
                return {
                  selectedSurveyRunIds: segment.responsesFilters?.surveyIds?.length
                    ? segment.responsesFilters?.surveyIds
                    : undefined,
                  selectedTestOptionIds: segment.responsesFilters?.testOptionIds?.length
                    ? segment.responsesFilters.testOptionIds
                    : null,
                  selectedDates:
                    segment.responsesFilters?.dateFrom || segment.responsesFilters?.dateTo
                      ? [segment.responsesFilters?.dateFrom, segment.responsesFilters?.dateTo]
                      : undefined,
                  selectedPanelType: segment.responsesFilters?.panelType ?? undefined,
                  selectedRegionId: segment.responsesFilters?.regionIds?.length
                    ? segment.responsesFilters.regionIds[0]
                    : undefined,
                  selectedFilterIds:
                    selectedFilters?.reduce(
                      (prev, cur) => {
                        if (!prev[cur.questionCode]) {
                          prev[cur.questionCode] = [];
                        }
                        prev[cur.questionCode]?.push(cur.id);
                        return prev;
                      },
                      {} as {
                        [x: string]: number[];
                      },
                    ) ?? undefined,
                  selectedQuestionOptionFilterIds: filterQuestion?.reduce(
                    (prev, c) => {
                      prev[c.questionId] = c.question.questionOptions
                        .filter(
                          (o) =>
                            segment.responsesFilters?.questionOptionsIds?.includes(o.id) ||
                            segment.responsesFilters?.excludeQuestionOptionsIds?.includes(o.id),
                        )
                        .reduce(
                          (p2, c2) => {
                            p2[c2.id] = !!segment.responsesFilters?.questionOptionsIds?.includes(c2.id);
                            return p2;
                          },
                          {} as {
                            [key in number]: boolean;
                          },
                        );
                      return prev;
                    },
                    {} as {
                      [x: number]: {
                        [x: number]: boolean;
                      };
                    },
                  ),
                  selectedProjectGroupSegmentIds: segment.responsesFilters?.projectGroupSegmentIds?.length
                    ? segment.responsesFilters.projectGroupSegmentIds
                    : undefined,
                } as ResultsModuleTypes.SegmentFeObj;
              }),
            )
          : of(null),
      ),
    );

  public readonly projectLastRunCost$ = this.shared$(
    this.project$.pipe(
      switchMap((p) => {
        if (!p?.projectSchedules) {
          return of(0);
        }

        const schedulesWithCreditLogs = p.projectSchedules.filter(
          (schedule) => schedule.creditLog?.creditAmount !== undefined,
        );

        if (schedulesWithCreditLogs.length === 0) {
          return of(0);
        }

        const schedulesByDate = schedulesWithCreditLogs.reduce(
          (groups, schedule) => {
            const date = schedule.scheduleDate;
            if (!groups[date]) {
              groups[date] = [];
            }
            groups[date].push(schedule);
            return groups;
          },
          {} as Record<string, typeof schedulesWithCreditLogs>,
        );

        const latestDate = Object.keys(schedulesByDate).sort().pop();

        if (!latestDate) {
          return of(0);
        }

        const latestGroupSchedules = schedulesByDate[latestDate];
        if (!latestGroupSchedules) {
          return of(0);
        }

        const totalCost = latestGroupSchedules.reduce((sum, schedule) => {
          return sum + (schedule.creditLog?.creditAmount ?? 0);
        }, 0);

        return of(totalCost * -1);
      }),
    ),
  );

  public setSegment = (segmentId: number) =>
    this.segmentToFEFilters$(segmentId).subscribe(
      (r) =>
        r &&
        this.manager.setSearchQuery({
          ...r,
          segmentId,
          selectedTestOptionIds: this.manager.getCurrentValue("selectedTestOptionIds"),
        }),
    );

  public updateSegment = () => {
    combineLatest([
      this.segmentId$.pipe(first()),
      this.selectedFilterIds$.pipe(first()),
      this.selectedQuestionOptionFilterIds$.pipe(first()),
      this.selectedPanelType$.pipe(first()),
      this.selectedRegionId$.pipe(first()),
      this.selectedSurveyRunIds$.pipe(first()),
      this.selectedDates$.pipe(first()),
    ])
      .pipe(
        switchMap(
          ([
            segmentId,
            filterIds,
            questionOptionFilterIds,
            panelType,
            regionId,
            selectedSurveyRunIds,
            selectedDates,
          ]) =>
            segmentId
              ? odataPatchOne(
                  "patchSegmentsOne",
                  {
                    filters: FiltersHelper.getResponsesFilters(
                      filterIds,
                      questionOptionFilterIds,
                      panelType,
                      regionId,
                      selectedSurveyRunIds,
                      selectedDates,
                    ),
                  },
                  { key: segmentId },
                ).$()
              : of(null),
        ),
      )
      .subscribe((r) => {
        if (r) {
          this.fetchSegments(r.id);
          toastSuccess(
            "Filter set saved",
            "All team members can access this filter set in the filters menu.",
          );
        }
      });
  };

  public deleteSegment = (segmentId: number) =>
    confirmDialog({
      appendTo: document.body,
      acceptClassName: "p-button-danger",
      header: "Delete filter set",
      acceptLabel: "Delete",
      message: `Are you sure you want to delete filter set "${this.currentState("segments")?.find((s) => s.id === segmentId)?.name ?? segmentId}"?`,
      accept: () =>
        odataDeleteOne("deleteSegmentsOne", { key: segmentId })
          .$()
          .subscribe(() => {
            this.fetchSegments();
            this.resetAllFilters();
          }),
    });

  public deleteNarrative = (narrativeId: number, title: string) =>
    confirmDialog({
      appendTo: document.body,
      acceptClassName: "p-button-danger",
      header: "Delete presentation",
      acceptLabel: "Delete",
      message: `Are you sure you want to delete presentation "${title}"?`,
      accept: () => {
        const deletedPath = `/narratives/${narrativeId}`;
        const currentPath = window.location.pathname;
        const viewingDeleted = currentPath.endsWith(deletedPath) || currentPath.includes(`${deletedPath}/`);
        odataDeleteOne("deleteProjectNarrativesOne", { key: narrativeId })
          .$()
          .subscribe(() => {
            this.refreshNarratives();
            if (!viewingDeleted) return;
            combineLatest([
              ResultsModuleRequests.getNarrativeIds$(this.projectId),
              ResultsModuleRequests.getNarrativeVersionExports$(this.projectId),
            ])
              .pipe(first())
              .subscribe(([narratives, exportRows]) => {
                if (narratives.length === 0) {
                  this.createReport((newId) =>
                    this.navigate(
                      ResultsModuleHelpers.internalResultsLink(this.projectId, `narratives/${newId}`),
                    ),
                  );
                  return;
                }
                const exports = exportRows.map((e) => ({
                  id: e.id,
                  narrativeId: e.projectNarrativeVersion?.projectNarrativeId ?? 0,
                }));
                const link = this.reportLandingLink(narratives, exports);
                if (link) void this.navigate(link);
              });
          });
      },
    });

  public cloneProject = () =>
    odataAct("actProjectsDuplicate", {}, { key: this.projectId })
      .$()
      .subscribe((r) => {
        toastSuccess("Project cloned", `Project '${r.projectName}' has been cloned.`);
        void this.navigate(getProjectLink(r));
      });

  public setSelectedBenchmarkType = (benchmarkTypeSelection: ResultsModuleTypes.BenchmarkTypeSelection) => {
    this.setState("benchmarkTypeSelection", benchmarkTypeSelection);
    if (benchmarkTypeSelection) {
      localStorage.setItem("benchmarkTypeSelection", JSON.stringify(benchmarkTypeSelection));
    } else {
      localStorage.removeItem("benchmarkTypeSelection");
    }
  };

  public setSplitBy = (splitBy: ResultsModuleTypes.SplitBy | null) => {
    this.selectedTestOptionIds$.pipe(first()).subscribe((testOptionIds) => {
      if (splitBy?.ids?.length && (testOptionIds?.length ?? 0) > 1) {
        this.setSelectedTestOptionId(testOptionIds!.slice(0, 1));
      }
      this.metricQuestions$.pipe(first()).subscribe((questions) => {
        this.manager.setSearchQuery({ splitBy });
        this.setState(
          "selectedMetricQuestionsIds",
          this.currentState("selectedMetricQuestionsIds")?.length
            ? [this.currentState("selectedMetricQuestionsIds")![0]!]
            : questions?.length
              ? [questions[0]!.questionId]
              : null,
        );
      });
    });
  };
  public setMetricVisualization = (metricVisualization: ResultsModuleTypes.MetricVisualization) => {
    if (metricVisualization === "top2combined") this.setState("benchmarkType", "top2");
    this.setStates({ metricVisualization });
  };
  public setBenchmarkType = (benchmarkType: ResultsModuleTypes.BenchmarkType) =>
    this.setStates({ benchmarkType });

  public setSelectedProjectGroupSegmentIds = (projectGroupSegmentIds: number[]) =>
    this.manager.setSearchQuery({
      selectedProjectGroupSegmentIds: projectGroupSegmentIds,
    });

  public toggleSelectedProjectGroupSegmentIds = (projectGroupSegmentId: number) =>
    this.manager.setSearchQuery({
      selectedProjectGroupSegmentIds: this.manager
        .getCurrentValueObject("selectedProjectGroupSegmentIds", [])
        .includes(projectGroupSegmentId)
        ? this.manager
            .getCurrentValueObject("selectedProjectGroupSegmentIds", [])
            .filter((id) => id !== projectGroupSegmentId)
        : [
            ...this.manager.getCurrentValueObject("selectedProjectGroupSegmentIds", []),
            projectGroupSegmentId,
          ],
    });

  public readonly updateProjectObjective = (summary: string) => {
    odataPostOne("postProjectObjectives", {
      projectId: this.projectId,
      summary,
    })
      .$()
      .subscribe((objective) => {
        this.mutateState("project", (prev) =>
          Object.assign({}, prev, {
            activeProjectObjective: { summary: objective.summary },
            activeProjectObjectiveId: objective.id,
          }),
        );
        this.setWithObjective(true);
        toast({
          severity: "success",
          summary: "Objective saved",
          detail:
            "We're currently analysing your objective and preparing summaries. This will take a few minutes.",
        });
        this.fetchProject();
      });
  };

  public deleteProjectObjective = (projectObjectiveId: number) =>
    confirmDialog({
      appendTo: document.body,
      acceptClassName: "p-button-danger",
      header: "Delete objective",
      acceptLabel: "Delete",
      message: "Are you sure you want to delete your custom objective?",
      accept: () => {
        this.setWithObjective(false);
        odataDeleteOne("deleteProjectObjectivesOne", { key: projectObjectiveId })
          .$()
          .subscribe(() => {
            this.mutateState("project", (prev) =>
              Object.assign({}, prev, { activeProjectObjective: null, activeProjectObjectiveId: null }),
            );
            this.fetchProject();
          });
      },
    });
}

const [ContextWrapper, useBloc] = createBLoC<BLoC>();

export const useResultsModuleBLoC = useBloc;

export const ResultsModuleBLoC: React.FC<BLoCCallback<BLoC>> = ({ children }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useMainNavigate();
  const manager = useQueryManager<T.QueryState>();
  const { setDownloadDate } = useChartDownloadButtonBLoC();
  const bloc = useInitBLoC(
    () => (projectId ? new BLoC(Number(projectId), navigate, manager, setDownloadDate) : null),
    [projectId],
  );
  return bloc ? <ContextWrapper key={projectId} bloc={bloc} children={children} /> : null;
};
